import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Shield, 
  Mail, 
  MessageCircle, 
  Loader2,
  Lock,
  ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { API } from "../App";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" or "code"
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [metodo, setMetodo] = useState("email");
  const [codigo, setCodigo] = useState("");

  // Check if already logged in
  useEffect(() => {
    const admin = localStorage.getItem("superAdmin");
    if (admin && localStorage.getItem("adminToken")) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Digite seu email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/request-code`, { email, metodo });
      toast.success(`Código enviado via ${metodo === "email" ? "Email" : "WhatsApp"}!`);
      setStep("code");
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao enviar código";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!codigo || codigo.length !== 6) {
      toast.error("Digite o código de 6 dígitos");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/verify-code`, { email, codigo });
      localStorage.setItem("superAdmin", JSON.stringify(response.data.admin));
      localStorage.setItem("adminToken", response.data.token);
      toast.success("Login realizado com sucesso!");
      navigate("/admin/dashboard");
    } catch (error) {
      const msg = error.response?.data?.detail || "Código inválido";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-orange-500" />
          </div>
          <CardTitle className="text-2xl font-heading text-white">
            SISTEMA DE ENCOMENDAS
          </CardTitle>
          <CardDescription className="text-slate-400">
            Painel Super Administrador
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "email" ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                    placeholder="seu@email.com"
                    data-testid="input-admin-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Receber código via:</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={metodo === "email" ? "default" : "outline"}
                    className={`flex-1 ${metodo === "email" ? "bg-orange-500 hover:bg-orange-600" : "border-slate-600 text-slate-300"}`}
                    onClick={() => setMetodo("email")}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={metodo === "whatsapp" ? "default" : "outline"}
                    className={`flex-1 ${metodo === "whatsapp" ? "bg-green-500 hover:bg-green-600" : "border-slate-600 text-slate-300"}`}
                    onClick={() => setMetodo("whatsapp")}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                data-testid="btn-request-code"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Enviar Código
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="text-center text-slate-300 mb-4">
                <p>Código enviado para:</p>
                <p className="font-semibold text-white">{email}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="codigo" className="text-slate-300">Código de Verificação</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <Input
                    id="codigo"
                    type="text"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="pl-11 h-14 bg-slate-700/50 border-slate-600 text-white text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                    data-testid="input-admin-code"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || codigo.length !== 6}
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                data-testid="btn-verify-code"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Verificar e Entrar
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-slate-400 hover:text-white"
                onClick={() => {
                  setStep("email");
                  setCodigo("");
                }}
              >
                Voltar
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
